/**
 * Settings → WhatsApp (Figma "Settings — WhatsApp click-to-chat"): a wa.me link to the business's
 * number, its QR code, and the floating chat button for the website. Nothing connects to a
 * WhatsApp account; the link just opens a chat with the number.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, CircleCheck } from "lucide-react";
import qrcode from "qrcode-generator";
import { useMemo, useState } from "react";

import { useOrganization } from "@/auth/organization";
import {
  Button,
  ErrorAlert,
  FullPageSpinner,
  Section,
  Toggle,
  Tooltip,
  buttonClass,
  cx,
  useToast,
} from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { portal } from "@/lib/api";
import { useFormDraft } from "@/lib/form-drafts";
import { ApiError } from "@/lib/http";
import { primarySite, siteKeys } from "@/lib/queries";
import type { ChatButtonPosition, WhatsAppSettings } from "@/lib/types";
import { useUnsavedChanges } from "@/lib/unsavedChanges";

import { COUNTRIES, flag } from "./options";
import { ReadOnlyNote } from "./parts";
import { checkNumber, grouped } from "./phone";
import { WhatsAppPreviewDialog } from "./WhatsAppPreview";

const MAX_MESSAGE = 200;
// PHP-4126: the two buttons do different things, and each says what (QA-39).
const TEST_LINK_HINT = "Opens WhatsApp with your number and message";
const PREVIEW_HINT = "Shows how the chat button will look on your website";
const NO_NUMBER = "Add your number to get a link";
const BAD_NUMBER = "Add a valid number to generate your link";

function chatLink(dial: string, digits: string, message: string): string {
  const base = `https://wa.me/${dial}${digits}`;
  return message.trim() ? `${base}?text=${encodeURIComponent(message.trim())}` : base;
}

export function WhatsAppTab() {
  const { orgId } = useOrganization();
  const settings = useQuery({ queryKey: ["whatsapp", orgId], queryFn: () => portal.whatsapp(orgId) });
  if (settings.isPending) return <FullPageSpinner />;
  if (settings.isError) return <ErrorAlert error={settings.error} />;
  return <WhatsAppForm key={orgId} saved={settings.data} />;
}

function WhatsAppForm({ saved }: { saved: WhatsAppSettings }) {
  const { orgId, current, can } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const editable = can("organization:update");
  const [country, setCountry] = useState(saved.country ?? "GB");
  const dial = COUNTRIES.find(([iso]) => iso === country)?.[1] ?? saved.dial_code ?? "44";
  const [number, setNumber] = useState(saved.number ? grouped(saved.number) : "");
  // The number is judged once the field has been left (or when a saved number is loaded), not while typing.
  const [judged, setJudged] = useState(Boolean(saved.number));
  const startingMessage = saved.configured
    ? saved.message
    : `Hi ${current.organization.name}! I’d like to ask about…`;
  const [message, setMessage] = useState(startingMessage);
  const [enabled, setEnabled] = useState(saved.button_enabled);
  const [position, setPosition] = useState<ChatButtonPosition>(saved.button_position);
  // The switches save at once; the number and message wait for a usable number, so those are what a draft keeps.
  const typed = useMemo(() => ({ country, number, message }), [country, number, message]);
  const kept = useFormDraft("settings.whatsapp", typed, { ready: editable });

  type Override = Partial<{ button_enabled: boolean; button_position: ChatButtonPosition }>;
  const save = useMutation({
    mutationFn: (over: Override) =>
      portal.saveWhatsapp(orgId, {
        country,
        dial_code: dial,
        number,
        message,
        button_enabled: enabled,
        button_position: position,
        ...over,
      }),
    onSuccess: (s) => {
      kept.clear();
      qc.setQueryData(["whatsapp", orgId], s);
      if (s.number) setNumber(grouped(s.number));
      toast.show({ id: "whatsapp-saved", tone: "success", title: "WhatsApp settings saved" });
    },
  });
  const publish = useMutation({
    mutationFn: () => portal.publishWhatsapp(orgId),
    onSuccess: (s) => {
      qc.setQueryData(["whatsapp", orgId], s);
      toast.show({ tone: "success", title: "WhatsApp button published" });
    },
  });
  const latest = save.data ?? saved;
  // PHP-4052: checked against the selected country's length rules, as the API does.
  const check = checkNumber(country, dial, number);
  const valid = check.state === "valid";
  const digits = check.state === "valid" ? check.national : number.replace(/\D/g, "");
  const noLink = check.state === "empty" ? NO_NUMBER : BAD_NUMBER;
  const unsaved =
    !latest.configured ||
    latest.number !== digits ||
    latest.dial_code !== dial ||
    latest.country !== country ||
    latest.message !== message.trim();
  // A form that was never saved is only "unsaved" once something in it was changed from what it started as.
  const edited = latest.configured
    ? unsaved
    : digits !== "" || country !== (saved.country ?? "GB") || message.trim() !== startingMessage.trim();
  const leaveGuard = useUnsavedChanges(editable && edited && !save.isPending);
  const link = valid ? chatLink(dial, digits, message) : null;
  const serverNumberError =
    save.error instanceof ApiError
      ? (save.error.fieldError("number") ??
        (save.error.status === 422 ? "Enter the full WhatsApp number" : null))
      : null;
  const numberError =
    (check.state === "invalid" && judged ? check.error : null) ?? (valid ? serverNumberError : null);

  // Saves when a field is left (or a switch changes) with something new and a usable number.
  const commit = (over?: Override) => {
    if (!editable || !valid) return;
    if (over || unsaved) save.mutate(over ?? {});
  };

  return (
    <div className="flex flex-col items-start gap-4 lg:flex-row">
      {leaveGuard.modal}
      <div className="flex w-full min-w-0 flex-1 flex-col gap-4">
        <Section variant="tight">
          <div className="flex items-center gap-3">
            <img src="/icons/settings/whatsapp.svg" alt="" className="h-9 w-[35.73px] shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <h2 className="text-base font-bold text-fg">WhatsApp click-to-chat</h2>
              <p className="text-[12.5px] leading-[1.55] text-fg-subtle">
                Let visitors message you on WhatsApp in one tap. This creates a link to your number. It
                doesn’t connect your WhatsApp account.
              </p>
            </div>
          </div>
          <ReadOnlyNote permission="organization:update" />
          <DraftOffer
            draft={kept}
            onContinue={(d) => {
              setCountry(d.country);
              setNumber(d.number);
              setMessage(d.message);
            }}
          />
          {unsaved && <DraftSavedNote savedAt={kept.savedAt} />}

          <div className="flex flex-col gap-2">
            <Label htmlFor="wa-number" text="WhatsApp number" note="Required" />
            <div className="flex gap-2.5">
              <span className="relative flex h-[41px] shrink-0 items-center gap-1.5 rounded-xl border border-border bg-surface px-3 text-[13.5px] font-semibold text-fg">
                {flag(country)} +{dial}
                <ChevronDown aria-hidden className="size-4 text-[#5c5551]" />
                <select
                  aria-label="Country"
                  value={country}
                  disabled={!editable}
                  onChange={(e) => setCountry(e.target.value)}
                  onBlur={() => commit()}
                  className="absolute inset-0 cursor-pointer opacity-0"
                >
                  {COUNTRIES.map(([iso, code, name]) => (
                    <option key={iso} value={iso}>
                      {flag(iso)} {name} (+{code})
                    </option>
                  ))}
                </select>
              </span>
              <input
                id="wa-number"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="7700 900123"
                value={number}
                disabled={!editable}
                onChange={(e) => setNumber(e.target.value)}
                onBlur={() => {
                  setJudged(true);
                  commit();
                }}
                aria-invalid={numberError ? true : undefined}
                aria-describedby="wa-number-help"
                className={cx(
                  "h-[41px] min-w-0 flex-1 rounded-xl bg-surface px-3.5 text-[13.5px] text-fg placeholder:text-fg-subtle disabled:opacity-60",
                  numberError
                    ? "border-[1.5px] border-red-500"
                    : "border-[1.5px] border-border focus:border-brand-500",
                )}
              />
            </div>
            {numberError ? (
              <p id="wa-number-help" className="text-xs text-red-600">
                {numberError}
              </p>
            ) : valid ? (
              <p
                id="wa-number-help"
                className="flex items-center gap-1.5 text-xs leading-[1.5] text-[#13734d]"
              >
                <CircleCheck aria-hidden className="size-[13px] shrink-0" />
                {save.isPending
                  ? "Saving…"
                  : // Italian numbers keep their 0, so the note only applies when one is dropped.
                    `${unsaved ? "Will be saved as" : "Saved as"} +${dial} ${grouped(digits)} · international format${digits.startsWith("0") ? "" : ", without the leading 0"}`}
              </p>
            ) : (
              <p id="wa-number-help" className="text-xs text-fg-subtle">
                The number customers will message, e.g. 07700 900123.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="wa-message"
              text="Prefilled message"
              note="Optional · visitors can edit it before sending"
            />
            <textarea
              id="wa-message"
              rows={2}
              maxLength={MAX_MESSAGE}
              value={message}
              disabled={!editable}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={() => commit()}
              className="h-[76px] w-full resize-none rounded-xl border border-border bg-surface px-3.5 py-[11px] text-[13.5px] leading-[1.55] text-fg focus:border-brand-500 disabled:opacity-60"
            />
            <p className="text-xs text-fg-subtle">
              {message.length} / {MAX_MESSAGE}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-semibold text-fg">Your chat link</p>
            <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-border bg-brand-50 py-2.5 pr-2.5 pl-3.5">
              <p className="min-w-0 flex-1 truncate text-[12.5px] text-[#5c5551]">{link ?? noLink}</p>
              <CopyButton text={link} reason={noLink} />
              <Tooltip content={link ? TEST_LINK_HINT : null} keepWrapper>
                <a
                  href={link ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={!link}
                  aria-describedby={link ? "wa-test-link-hint" : undefined}
                  title={link ? undefined : noLink}
                  className={cx(buttonClass("secondary", "pill"), !link && "pointer-events-none opacity-60")}
                >
                  Test link ↗
                </a>
              </Tooltip>
              {link && (
                <span id="wa-test-link-hint" className="sr-only">
                  {TEST_LINK_HINT}
                </span>
              )}
            </div>
          </div>
          <ErrorAlert error={numberError ? null : save.error} />
        </Section>

        <ChatButtonCard
          enabled={enabled}
          position={position}
          numberValid={valid}
          link={link}
          onEnabled={(on) => {
            setEnabled(on);
            commit({ button_enabled: on });
          }}
          onPosition={(p) => {
            setPosition(p);
            commit({ button_position: p });
          }}
          settingsSavedAt={latest.updated_at}
          savePending={save.isPending}
          publishStatus={latest.publish_status}
          publishError={latest.publish_error}
          onPublish={() => publish.mutate()}
          publishPending={publish.isPending}
          publishFailure={publish.error}
        />
      </div>

      <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
        <Section variant="tight">
          <h2 className="text-[15px] font-bold text-fg">Preview</h2>
          <div className="flex flex-col gap-2.5 rounded-[22px] bg-[#ede5d9] p-3.5">
            <p className="truncate rounded-xl bg-[#085e54] px-3 py-2.5 text-[13px] font-semibold text-white">
              {current.organization.name}
            </p>
            {message.trim() ? (
              <p className="rounded-xl bg-[#dbf7c7] px-3 py-2.5 text-[12.5px] leading-[1.5] break-words text-fg">
                {message.trim()}
              </p>
            ) : (
              <p className="rounded-xl bg-white/60 px-3 py-2.5 text-[12.5px] text-fg-subtle">
                No message: visitors start with an empty chat.
              </p>
            )}
            <p className="text-center text-[11.5px] text-fg-subtle">Visitor sees this ready to send</p>
          </div>
        </Section>
        <QrCard link={link} name={current.organization.name} reason={noLink} />
      </div>
    </div>
  );
}

function Label({ htmlFor, text, note }: { htmlFor: string; text: string; note?: string }) {
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-fg">
        {text}
      </label>
      {note && <span className="truncate text-xs text-fg-subtle">{note}</span>}
    </div>
  );
}

function CopyButton({ text, reason }: { text: string | null; reason: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      size="pill"
      disabledReason={text ? undefined : reason}
      onClick={() => {
        if (!text) return;
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

// ── chat button on the website ──────────────────────────────────────────────
function ChatButtonCard({
  enabled,
  position,
  numberValid,
  link,
  onEnabled,
  onPosition,
  settingsSavedAt,
  savePending,
  publishStatus,
  publishError,
  onPublish,
  publishPending,
  publishFailure,
}: {
  enabled: boolean;
  position: ChatButtonPosition;
  /** The chat button needs a usable number before it can be switched on or moved. */
  numberValid: boolean;
  link: string | null;
  onEnabled: (on: boolean) => void;
  onPosition: (p: ChatButtonPosition) => void;
  settingsSavedAt: string | null;
  /** A save is on its way; the preview shows saved settings only. */
  savePending: boolean;
  publishStatus: WhatsAppSettings["publish_status"];
  publishError: string | null;
  onPublish: () => void;
  publishPending: boolean;
  publishFailure: unknown;
}) {
  const { orgId } = useOrganization();
  const sites = useQuery({ queryKey: ["sites", orgId], queryFn: () => portal.sites(orgId) });
  const site = sites.data ? primarySite(sites.data.sites) : undefined;
  const deployments = useQuery({
    queryKey: siteKeys.deployments(orgId, site?.id ?? ""),
    queryFn: () => portal.deployments(site!.id),
    enabled: Boolean(site),
  });
  const live = deployments.data?.deployments.find((d) => d.is_live || d.id === site?.live_deployment_id);
  // The button reaches the live site with the next publish after the settings changed.
  const liveAt = live ? Date.parse(live.finished_at ?? live.created_at ?? "") : 0;
  const pending =
    publishStatus !== "published" && Boolean(settingsSavedAt && Date.parse(settingsSavedAt) > liveAt);
  const [previewOpen, setPreviewOpen] = useState(false);
  const builtSites = sites.data?.sites.filter((s) => s.kind !== "external") ?? [];
  // Why the preview can't be shown (QA-05): it needs a working link, a live site and the latest settings saved.
  const previewUnavailable = !link
    ? "Add a valid WhatsApp number first."
    : !live
      ? "Preview unavailable: your site has no published version yet."
      : savePending
        ? "Saving your changes…"
        : null;

  return (
    <Section variant="tight">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className="text-[15px] font-bold text-fg">Chat button on your website</h2>
          <p className="text-[12.5px] text-fg-subtle">A floating WhatsApp button on every page</p>
        </div>
        <Toggle
          size="md"
          label="Chat button on your website"
          checked={enabled}
          requires="organization:update"
          disabledReason={numberValid ? undefined : "Add a valid WhatsApp number first."}
          onChange={onEnabled}
        />
      </div>
      <div className="flex items-center gap-2" role="radiogroup" aria-label="Position">
        <span className="text-[13px] font-semibold text-fg">Position</span>
        {(
          [
            ["bottom_right", "Bottom right"],
            ["bottom_left", "Bottom left"],
          ] as const
        ).map(([p, label]) => (
          <Button
            unstyled
            key={p}
            role="radio"
            aria-checked={position === p}
            requires="organization:update"
            disabledReason={
              !numberValid
                ? "Add a valid WhatsApp number first."
                : enabled
                  ? undefined
                  : "Turn on the chat button to choose where it sits."
            }
            onClick={() => onPosition(p)}
            className={cx(
              "rounded-full border px-3 py-1.5 text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-60",
              position === p
                ? "border-brand-500 bg-[#fef3f0] text-brand-500"
                : "border-border bg-surface text-[#5c5551]",
            )}
          >
            {label}
          </Button>
        ))}
      </div>
      {site && enabled && (pending || !live) && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-[#fce7c6] px-3.5 py-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-[13.5px] font-semibold text-fg">In your draft · not on the live site yet</p>
            <p className="text-xs text-[#5c5551]">
              {/* PHP-4126: publishing updates the chat button on the live version; it creates no new version. */}
              {live
                ? builtSites.length > 1
                  ? "Your live websites don’t show this change yet. Publishing updates all of them."
                  : "Your live site doesn’t show this change yet. Publishing updates it."
                : "Your website isn’t live yet. The button appears once you publish it."}
            </p>
          </div>
          <Button
            variant="secondary"
            size="pill"
            disabledReason={previewUnavailable ?? undefined}
            hint={PREVIEW_HINT}
            aria-haspopup="dialog"
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
          <Button
            unstyled
            type="button"
            onClick={onPublish}
            disabled={publishPending}
            disabledReason={numberValid ? undefined : "Add a valid WhatsApp number first."}
            aria-busy={publishPending}
            className={buttonClass("brand", "pill")}
          >
            {publishPending ? "Publishing…" : "Publish to live site"}
          </Button>
        </div>
      )}
      {site && (!enabled || (!pending && Boolean(live))) && (
        <Button
          unstyled
          type="button"
          onClick={onPublish}
          disabled={publishPending}
          disabledReason={numberValid ? undefined : "Add a valid WhatsApp number first."}
          aria-busy={publishPending}
          className={buttonClass("secondary", "pill")}
        >
          {publishPending ? "Publishing…" : "Publish latest settings"}
        </Button>
      )}
      <ErrorAlert error={publishFailure} />
      {publishStatus === "published" && <p className="text-xs text-[#13734d]">Published to live websites.</p>}
      {publishStatus === "failed" && (
        <p className="text-xs text-red-600">{publishError ?? "Publication failed."}</p>
      )}
      <p className="text-xs leading-[1.5] text-fg-subtle">
        Turning the button off removes it at the next publish. Your number and link stay saved.
      </p>
      {site && link && (
        <WhatsAppPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          site={site}
          siteCount={builtSites.length}
          settingsSavedAt={settingsSavedAt}
          onPublish={onPublish}
          publishPending={publishPending}
        />
      )}
    </Section>
  );
}

// ── QR code ─────────────────────────────────────────────────────────────────
const QR_CELL = 4;
const QR_MARGIN = 2;

function QrCard({ link, name, reason }: { link: string | null; name: string; reason: string }) {
  const qr = useMemo(() => {
    if (!link) return null;
    const q = qrcode(0, "M");
    q.addData(link, "Byte");
    q.make();
    return q;
  }, [link]);
  const svg = useMemo(
    () => (qr ? qr.createSvgTag({ cellSize: QR_CELL, margin: QR_MARGIN, scalable: true }) : ""),
    [qr],
  );
  const file = `${
    name
      .replace(/[^\w-]+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase() || "whatsapp"
  }-whatsapp-qr`;

  const download = (kind: "png" | "svg") => {
    if (!qr) return;
    if (kind === "svg") {
      save(new Blob([svg], { type: "image/svg+xml" }), `${file}.svg`);
      return;
    }
    // A print-friendly PNG: 12px modules.
    const cell = 12;
    const size = (qr.getModuleCount() + QR_MARGIN * 2) * cell;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = "#000";
    for (let r = 0; r < qr.getModuleCount(); r++)
      for (let c = 0; c < qr.getModuleCount(); c++)
        if (qr.isDark(r, c)) ctx.fillRect((c + QR_MARGIN) * cell, (r + QR_MARGIN) * cell, cell, cell);
    canvas.toBlob((blob) => blob && save(blob, `${file}.png`), "image/png");
  };

  return (
    <Section variant="tight">
      <h2 className="text-[15px] font-bold text-fg">QR code</h2>
      <p className="text-[12.5px] leading-[1.55] text-fg-subtle">
        Print it on cards, bags or your shop window. Scanning opens the chat.
      </p>
      <div className="flex justify-center">
        <div
          className="flex size-[140px] items-center justify-center overflow-hidden rounded-[10px] border border-border bg-white p-1.5 [&>svg]:size-full"
          role="img"
          aria-label={link ? "QR code for your chat link" : reason}
          // The SVG is generated locally from the link (no user markup reaches it).
          dangerouslySetInnerHTML={qr ? { __html: svg } : undefined}
        >
          {qr ? undefined : <span className="px-3 text-center text-xs text-fg-subtle">{reason}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="pill"
          disabledReason={qr ? undefined : reason}
          onClick={() => download("png")}
        >
          Download PNG
        </Button>
        <Button
          variant="secondary"
          size="pill"
          disabledReason={qr ? undefined : reason}
          onClick={() => download("svg")}
        >
          Download SVG
        </Button>
      </div>
      <p className="text-xs leading-[1.5] text-fg-subtle">
        The QR code updates when you change the number or message.
      </p>
    </Section>
  );
}

function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
